import { useNavigate } from 'react-router';
import React, { useRef, useState, useEffect } from 'react';

import { alpha } from '@mui/material/styles';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import { Box, Card, Checkbox, useTheme, IconButton, Typography } from '@mui/material';

import DLXDriveTag from './DLXDriveTag';
import DLXDriveStatus from './DLXDriveStatus';
import useLocales from '../../hooks/useLocales';
import { useSelector } from '../../redux/store';
import { PATH_STANDARD } from '../../routes/paths';
import DLXDriveDocumentActionsMenu from './settings/DLXDriveDocumentActionsMenu';
import { DLXDriveDocumentType, DLXDriveSelectedItem } from '../../@types/dlx-drive/types';
import { addItemState, removeItemState, setCardWidthState } from '../../redux/slices/dlxDrive';
import {
  FOLDER_ICON_LARGE_SIZE,
  FOLDER_ICON_SMALL_SIZE,
  WIDTH_CORRECTION_LARGE_SCREEN,
  WIDTH_CORRECTION_SMALL_SCREEN,
} from '../../utils/constants/dlxDriveConstants';

// ----------------------------------------------------------------

type DLXDriveDocumentCardProps = {
  documentData: DLXDriveDocumentType;
  isLargeScreen: boolean;
  isTagsEnabled: boolean;
  index: number;
};

const DLXDriveDocumentCard: React.FC<DLXDriveDocumentCardProps> = ({
  documentData,
  isLargeScreen,
  isTagsEnabled,
  index,
}) => {
  const { t } = useLocales();
  const theme = useTheme();
  const [isMenuPopoverOpen, setIsMenuPopoverOpen] = useState<boolean>(false);
  const { checkedItems, cardWidth } = useSelector((state) => state.dlxDrive);
  const {
    id,
    name,
    ownerEmail,
    ownerFullName,
    extension,
    modified,
    prefixName,
    status,
    tags,
    actions,
  } = documentData;
  const navigate = useNavigate();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const settingsRef = useRef<HTMLElement>(null);

  const isChecked = checkedItems.filter((item) => item.id === id && !item.isFolder).length > 0;
  const checkedColor = theme.palette.primary.main;
  const iconSize = isLargeScreen ? `${FOLDER_ICON_LARGE_SIZE}px` : `${FOLDER_ICON_SMALL_SIZE}px`;
  const iconExtensionSize = isLargeScreen ? `${40}px` : `${FOLDER_ICON_SMALL_SIZE}px`;
  const isLight = theme.palette.mode === 'light';

  const handleDocumentClick = () => {
    navigate(`${PATH_STANDARD.documents}/${id}`);
  };

  const handleWindowResize = () => {
    if (cardRef.current) {
      const newCardWidth = cardRef.current.offsetWidth;

      if (newCardWidth !== cardWidth) {
        setCardWidthState(cardRef.current.offsetWidth);
      }
    }
  };

  const handleCheckboxChange = () => {
    const item: DLXDriveSelectedItem = {
      isFolder: false,
      id,
      name,
      status,
      actions,
    };

    if (isChecked) {
      removeItemState(item);
    } else {
      addItemState(item);
    }
  };

  const handleSettingsClick = () => {
    setIsMenuPopoverOpen(true);
  };

  window.addEventListener('resize', handleWindowResize);

  useEffect(() => {
    handleWindowResize();
  }, [cardRef.current]);

  return (
    <>
      <Card
        ref={cardRef}
        sx={[{
          p: 1
        }, isChecked ? {
          backgroundColor: alpha(checkedColor, 0.1)
        } : {
          backgroundColor: null
        }, isChecked ? {
          border: `1px solid ${alpha(checkedColor, 0.5)}`
        } : {
          border: '1px solid transparent'
        }, isLight ? {
          '&:hover': {
            backgroundColor: theme.palette.secondary.lighter
          }
        } : {
          '&:hover': {
            backgroundColor: theme.palette.secondary.darker
          }
        }]}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box>
            <Checkbox
              id={`docCardCheckBox_${index}`}
              checked={isChecked}
              onChange={handleCheckboxChange}
            />
          </Box>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            onClick={handleDocumentClick}
          >
            {/*-----------------------------*/}
            {/* Card view for LARGE screens */}
            {/*-----------------------------*/}

            {isLargeScreen && (
              <>
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'left',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      display: 'block',
                      minWidth: iconSize,
                      minHeight: iconSize,
                      width: iconSize,
                      height: iconSize,
                      pt: 1,
                    }}
                  >
                    <img
                      src={
                        extension.length <= 5
                          ? `/icons/icon_${extension}.svg`
                          : `/icons/icon_pdf.svg`
                      }
                      style={{ height: iconExtensionSize, width: iconExtensionSize }}
                      alt={extension}
                    />
                  </Box>

                  <Box
                    sx={[{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5
                    }, cardWidth ? {
                      width: `${cardWidth - WIDTH_CORRECTION_LARGE_SCREEN}px`
                    } : {
                      width: '100%'
                    }]}
                  >
                    <Typography variant="subtitle1" noWrap>
                      {name}
                    </Typography>

                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ flexGrow: 1, flexShrink: 1, flexBasis: '0%' }}>
                        <Typography variant="body2" color={theme.palette.text.secondary}>
                          {`${t('documentTree.table.modified')}: ${modified.split('T')[0]}`}
                        </Typography>
                      </Box>

                      <Box sx={{ flexGrow: 1, flexShrink: 1, flexBasis: '0%', overflow: 'hidden' }}>
                        {prefixName && (
                          <Typography variant="body2" color={theme.palette.text.secondary} noWrap>
                            {`${t('documentTree.table.prefix')}: ${prefixName}`}
                          </Typography>
                        )}
                      </Box>

                      {/* ---- doc owner --- */}
                      <Box
                        sx={{
                          flexGrow: 1,
                          flexShrink: 1,
                          flexBasis: '0%',
                        }}
                      >
                        <Typography variant="body2" color={theme.palette.text.secondary}>
                          {ownerFullName
                            ? `${t('documents.owner')}: ${ownerFullName}`
                            : `${t('documents.owner')}: ${ownerEmail}`}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          flexGrow: 1,
                          flexShrink: 1,
                          flexBasis: '0%',
                          display: 'flex',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <DLXDriveStatus status={status} />
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {isTagsEnabled && tags.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      textAlign: 'center',
                      flexWrap: 'wrap',
                      gap: 1,
                      my: 1,
                      pl: '5px',
                    }}
                  >
                    {tags.map((tag, index) => (
                      <DLXDriveTag key={`folder-tag-${index}`} tag={tag} />
                    ))}
                  </Box>
                )}
              </>
            )}

            {/*-----------------------------*/}
            {/* Card view for SMALL screens */}
            {/*-----------------------------*/}

            {!isLargeScreen && (
              <Box
                sx={[{
                  display: 'flex',
                  flexDirection: 'column'
                }, cardWidth ? {
                  width: `${cardWidth - WIDTH_CORRECTION_SMALL_SCREEN}px`
                } : {
                  width: '100%'
                }]}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'left',
                    alignItems: 'center',
                    gap: 1,
                    my: 1,
                  }}
                >
                  <img
                    src={
                      extension.length <= 5 ? `/icons/icon_${extension}.svg` : `/icons/icon_pdf.svg`
                    }
                    width={iconSize}
                    height={iconSize}
                    alt={extension}
                  />

                  <Typography variant="subtitle1" noWrap>
                    {name}
                  </Typography>
                </Box>

                <Typography variant="body2" color={theme.palette.text.secondary}>
                  {`${t('documentTree.table.modified')}: ${modified.split('T')[0]}`}
                </Typography>

                <Typography variant="body2" color={theme.palette.text.secondary} noWrap>
                  {`${t('documentTree.table.prefix')}: ${prefixName || '-'}`}
                </Typography>

                {/* doc owner */}
                <Typography variant="body2" color={theme.palette.text.secondary}>
                  {ownerFullName
                    ? `${t('documents.owner')}: ${ownerFullName}`
                    : `${t('documents.owner')}: ${ownerEmail}`}
                </Typography>

                {/* doc sign status */}
                <Box sx={{ my: 1 }}>
                  <DLXDriveStatus status={status} />
                </Box>

                {isTagsEnabled && tags.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      textAlign: 'center',
                      flexWrap: 'wrap',
                      gap: 1,
                      mt: 1,
                      mb: 0.5,
                    }}
                  >
                    {tags.map((tag, index) => (
                      <DLXDriveTag key={`folder-tag-${index}`} tag={tag} />
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>

          <Box ref={settingsRef}>
            <IconButton size="small" onClick={handleSettingsClick}>
              <MoreVertRoundedIcon />
            </IconButton>
          </Box>
        </Box>
      </Card>
      <DLXDriveDocumentActionsMenu
        isMenuPopoverOpen={isMenuPopoverOpen}
        setIsMenuPopoverOpen={setIsMenuPopoverOpen}
        anchorEl={settingsRef}
        documentId={id}
        documentName={name}
        documentStatus={status}
        actions={actions}
      />
    </>
  );
};

export default DLXDriveDocumentCard;
